import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAppData } from '../../contexts/AppDataContext';
import { useCompanySettings } from '../../contexts/CompanySettingsContext';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ChartBarIcon,
  StarIcon,
  DocumentArrowDownIcon
} from '@heroicons/react/24/outline';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const ProductPerformanceAnalysis = () => {
  const navigate = useNavigate();
  const { products, sales, purchases } = useAppData();
  const { companySettings } = useCompanySettings();
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedTimeframe, setSelectedTimeframe] = useState('month');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [productPerformanceData, setProductPerformanceData] = useState([]);
  const [categoryPerformanceData, setCategoryPerformanceData] = useState([]);
  const [profitabilityData, setProfitabilityData] = useState([]);
  const [productRankings, setProductRankings] = useState([]);
  const [categories, setCategories] = useState([]);

  // Couleurs pour les graphiques (utilisées par le radar chart)
  const chartColors = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  // Extraire les catégories uniques des produits
  useEffect(() => {
    if (products.length > 0) {
      const uniqueCategories = [...new Set(products.map(product => product.category))].filter(Boolean);
      setCategories(['all', ...uniqueCategories]);
    }
  }, [products]);

  // Transform sales and purchases data for consistency
  const transformedSalesData = useMemo(() => {
    if (!sales || !sales.length) return [];
    
    try {
      const { transformSalesData } = require('../../utils/salesDataUtils');
      return transformSalesData(sales);
    } catch (error) {
      console.error("Error transforming sales data:", error);
      return sales;
    }
  }, [sales]);
  
  const transformedPurchasesData = useMemo(() => {
    if (!purchases || !purchases.length) return [];
    
    try {
      const { transformPurchasesData } = require('../../utils/purchasesDataUtils');
      return transformPurchasesData(purchases);
    } catch (error) {
      console.error("Error transforming purchases data:", error);
      return purchases;
    }
  }, [purchases]);

  // Définition de generateAnalyticsData
  const generateAnalyticsData = useCallback(() => {
    // Filtrer les produits par catégorie si nécessaire
    const filteredProducts = selectedCategory === 'all'
      ? products
      : products.filter(product => product.category === selectedCategory);
  
    // Générer les données de performance des produits
    const performanceData = filteredProducts.map(product => {
      // Calculer les ventes totales du produit
      const productSales = transformedSalesData.reduce((total, sale) => {
        if (!sale.items || !Array.isArray(sale.items)) return total;
        
        const productItems = sale.items.filter(item => 
          (item.productId === product.id || item.id === product.id)
        );
        
        const itemsTotal = productItems.reduce((sum, item) => {
          const quantity = parseFloat(item.quantity) || 0;
          const price = parseFloat(item.price) || 0;
          const itemTotal = parseFloat(item.total) || (quantity * price);
          return sum + itemTotal;
        }, 0);
        
        return total + itemsTotal;
      }, 0);

      // Calculer les achats totaux du produit
      const productPurchases = transformedPurchasesData.reduce((total, purchase) => {
        if (!purchase.items || !Array.isArray(purchase.items)) return total;
        
        const productItems = purchase.items.filter(item => 
          (item.productId === product.id || item.id === product.id)
        );
        
        const itemsTotal = productItems.reduce((sum, item) => {
          const quantity = parseFloat(item.quantity) || 0;
          const price = parseFloat(item.unitCost || item.price) || 0;
          const itemTotal = parseFloat(item.total) || (quantity * price);
          return sum + itemTotal;
        }, 0);
        
        return total + itemsTotal;
      }, 0);

      // Calculer la marge brute
      const grossProfit = productSales - productPurchases;
      const profitMargin = productSales > 0 ? (grossProfit / productSales) * 100 : 0;

      // Calculer le taux de rotation (réel)
      // Calculer les unités vendues
      const unitsSold = transformedSalesData.reduce((total, sale) => {
        if (!sale.items || !Array.isArray(sale.items)) return total;
        
        const productItems = sale.items.filter(item => 
          (item.productId === product.id || item.id === product.id)
        );
        
        const totalQuantity = productItems.reduce((sum, item) => {
          return sum + (parseFloat(item.quantity) || 0);
        }, 0);
        
        return total + totalQuantity;
      }, 0);
      
      // Stock moyen (utilise le stock actuel comme approximation)
      const avgStock = parseFloat(product.stock) || 1;
      
      // Taux de rotation = Unités vendues / Stock moyen
      const stockTurnover = unitsSold / avgStock;

      // Calculer la tendance des ventes (en comparant les 3 derniers mois)
      
      // Obtenir les dates pour les 3 et 6 derniers mois
      const today = new Date();
      const threeMonthsAgo = new Date();
      threeMonthsAgo.setMonth(today.getMonth() - 3);
      
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(today.getMonth() - 6);
      
      // Filtrer les ventes des 3 derniers mois
      const recent3MonthsSales = transformedSalesData.filter(sale => {
        const saleDate = new Date(sale.rawDate);
        return saleDate >= threeMonthsAgo;
      });
      
      // Filtrer les ventes des 3 mois précédents
      const previous3MonthsSales = transformedSalesData.filter(sale => {
        const saleDate = new Date(sale.rawDate);
        return saleDate >= sixMonthsAgo && saleDate < threeMonthsAgo;
      });
      
      // Calculer les ventes du produit pour chaque période
      const recent3MonthsTotal = recent3MonthsSales.reduce((total, sale) => {
        if (!sale.items || !Array.isArray(sale.items)) return total;
        
        const productItems = sale.items.filter(item => 
          (item.productId === product.id || item.id === product.id)
        );
        
        const itemsTotal = productItems.reduce((sum, item) => {
          return sum + (parseFloat(item.quantity) || 0);
        }, 0);
        
        return total + itemsTotal;
      }, 0);
      
      const previous3MonthsTotal = previous3MonthsSales.reduce((total, sale) => {
        if (!sale.items || !Array.isArray(sale.items)) return total;
        
        const productItems = sale.items.filter(item => 
          (item.productId === product.id || item.id === product.id)
        );
        
        const itemsTotal = productItems.reduce((sum, item) => {
          return sum + (parseFloat(item.quantity) || 0);
        }, 0);
        
        return total + itemsTotal;
      }, 0);
      
      // Calculer la tendance (% de changement)
      let trendValue = 0;
      let trend = 'stable';
      
      if (previous3MonthsTotal > 0) {
        trendValue = ((recent3MonthsTotal - previous3MonthsTotal) / previous3MonthsTotal) * 100;
        trend = trendValue >= 0 ? 'up' : 'down';
      } else if (recent3MonthsTotal > 0) {
        trend = 'up';
        trendValue = 100; // Nouveau produit avec des ventes
      }
      
      // Limiter la valeur de la tendance à une plage raisonnable
      trendValue = Math.min(Math.max(Math.abs(trendValue), 0), 100);

      // Calculer la note de performance globale (sur 100)
      const performanceScore = Math.min(100, Math.max(0, 
        (profitMargin * 0.4) + // 40% basé sur la marge
        (Math.min(stockTurnover, 10) * 3) + // 30% basé sur la rotation (plafonné à 10)
        (unitsSold > 0 ? 15 : 0) + // 15% si le produit a des ventes
        (trend === 'up' ? 15 : 0) // 15% si la tendance est positive
      ));

      return {
        id: product.id,
        name: product.name,
        category: product.category || 'Non catégorisé',
        sales: productSales,
        purchases: productPurchases,
        profit: grossProfit,
        margin: profitMargin.toFixed(1),
        turnover: stockTurnover.toFixed(1),
        score: Math.round(performanceScore),
        trend: trend,
        trendValue: Math.round(trendValue),
        unitsSold: unitsSold
      };
    });

    // Trier par score de performance
    const sortedPerformance = [...performanceData].sort((a, b) => b.score - a.score);
    setProductPerformanceData(sortedPerformance);

    // Générer les données de performance par catégorie
    const categoryData = {};
    performanceData.forEach(product => {
      if (!categoryData[product.category]) {
        categoryData[product.category] = {
          name: product.category,
          totalSales: 0,
          totalProfit: 0,
          productCount: 0,
          averageScore: 0
        };
      }
      
      categoryData[product.category].totalSales += product.sales;
      categoryData[product.category].totalProfit += product.profit;
      categoryData[product.category].productCount += 1;
      categoryData[product.category].averageScore += product.score;
    });

    // Calculer les moyennes par catégorie
    Object.values(categoryData).forEach(category => {
      category.averageScore = Math.round(category.averageScore / category.productCount);
    });

    setCategoryPerformanceData(Object.values(categoryData));

    // Générer les données de profitabilité réelles basées sur les ventes et achats
    const profitData = [];
    const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
    
    // Obtenir l'année actuelle
    const currentYear = new Date().getFullYear();
    
    // Initialiser les données pour chaque mois
    for (let i = 0; i < 12; i++) {
      profitData.push({
        month: months[i],
        profit: 0,
        sales: 0
      });
    }
    
    // Agréger les ventes par mois
    transformedSalesData.forEach(sale => {
      const saleDate = new Date(sale.rawDate);
      
      // Ne considérer que les ventes de l'année en cours
      if (saleDate.getFullYear() === currentYear) {
        const monthIndex = saleDate.getMonth();
        profitData[monthIndex].sales += parseFloat(sale.rawAmount) || 0;
      }
    });
    
    // Agréger les achats par mois et calculer les profits
    transformedPurchasesData.forEach(purchase => {
      const purchaseDate = new Date(purchase.rawDate);
      
      // Ne considérer que les achats de l'année en cours
      if (purchaseDate.getFullYear() === currentYear) {
        const monthIndex = purchaseDate.getMonth();
        const purchaseAmount = parseFloat(purchase.rawAmount) || 0;
        
        // Le profit est calculé comme les ventes moins les achats
        profitData[monthIndex].profit = profitData[monthIndex].sales - purchaseAmount;
      }
    });
    
    // S'assurer que tous les mois ont des valeurs de profit calculées
    profitData.forEach(data => {
      if (!data.profit) {
        // Si pas d'achats enregistrés, le profit est égal aux ventes (marge de 100%)
        data.profit = data.sales;
      }
    });
    setProfitabilityData(profitData);

    // Générer les classements de produits
    const topProducts = sortedPerformance.slice(0, 5).map((product, index) => ({
      ...product,
      rank: index + 1
    }));

    const bottomProducts = [...sortedPerformance]
      .sort((a, b) => a.score - b.score)
      .slice(0, 5)
      .map((product, index) => ({
        ...product,
        rank: sortedPerformance.length - index
      }));

    setProductRankings({ top: topProducts, bottom: bottomProducts });
  }, [products, transformedSalesData, transformedPurchasesData, selectedCategory]);

  // Charger les données quand les dépendances changent
  useEffect(() => {
    const timer = setTimeout(() => {
      generateAnalyticsData();
      setIsLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [generateAnalyticsData]);

  const getPerformanceColor = (score) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-blue-400';
    if (score >= 40) return 'text-yellow-400';
    return 'text-red-400';
  };

  // PDF Report Generation
  const handleGenerateReport = async () => {
    setIsGenerating(true);
    
    try {
      console.log('🔄 Starting Product Performance PDF generation...');
      console.log('📊 Data check:', {
        productsCount: products?.length || 0,
        salesCount: sales?.length || 0,
        purchasesCount: purchases?.length || 0,
        productPerformanceDataCount: productPerformanceData?.length || 0
      });
      
      // Minimalist red theme colors (consistent with other reports)
      const primaryRed = [185, 28, 28]; // #B91C1C - True red color
      const secondaryRed = [153, 27, 27]; // #991B1B - Darker red
      const lightGray = [248, 248, 248]; // Very light gray for subtle backgrounds
      
      // Create new PDF document
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      let currentPage = 1;
      
      // Helper function to add minimal header and footer
      const addHeaderFooter = async () => {
        // HEADER - Company logo and name
        let headerYPosition = 10;
        
        // Add company logo if available
        if (companySettings?.logo) {
          try {
            // Add logo to PDF (small size: 15mm width, proportional height)
            pdf.addImage(companySettings.logo, 'PNG', 20, 5, 15, 10);
            
            // Position company name next to logo
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(0, 0, 0); // Black color
            const companyName = companySettings?.companyName || 'INVENTAIRE V2';
            pdf.text(companyName, 40, 12);
            
            headerYPosition = 18; // Adjust for logo height
          } catch (error) {
            console.log('Error adding logo to PDF:', error);
            // Fallback to text-only header
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(0, 0, 0); // Black color
            const companyName = companySettings?.companyName || 'INVENTAIRE V2';
            pdf.text(companyName, 20, 10);
          }
        } else {
          // No logo - just company name
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(0, 0, 0); // Black color
          const companyName = companySettings?.companyName || 'INVENTAIRE V2';
          pdf.text(companyName, 20, 10);
        }
        
        // Simple thin line below header
        pdf.setDrawColor(...primaryRed);
        pdf.setLineWidth(0.2);
        pdf.line(20, headerYPosition, pageWidth - 20, headerYPosition);
        
        // FOOTER - Software name and page number
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(120, 120, 120);
        pdf.text('INVENTAIRE V2', 20, pageHeight - 8);
        
        // Page number
        pdf.text(`${currentPage}`, pageWidth - 25, pageHeight - 8);
      };
      
      // Helper function to add minimal section header
      const addSectionHeader = async (title, yPos) => {
        // Check if we need a new page
        if (yPos > pageHeight - 40) {
          pdf.addPage();
          currentPage++;
          await addHeaderFooter();
          yPos = 30; // Adjust for new header size
        }
        
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 0, 0); // Black color
        pdf.text(title, 20, yPos);
        
        // Thin underline
        pdf.setDrawColor(...primaryRed);
        pdf.setLineWidth(0.1);
        const textWidth = pdf.getTextWidth(title);
        pdf.line(20, yPos + 2, 20 + textWidth, yPos + 2);
        
        return yPos + 8; // Return new position after header
      };
      
      // Start first page
      await addHeaderFooter();
      let yPosition = 30; // Start below larger header with logo
      
      // Main Title
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0); // Black color
      pdf.text('ANALYSE DE PERFORMANCE DES PRODUITS', 20, yPosition);
      yPosition += 8;
      
      // Date and time
      pdf.setFontSize(9);
      pdf.setTextColor(120, 120, 120);
      pdf.text(`Généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}`, 20, yPosition);
      yPosition += 20;
      
      // Filters summary
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(60, 60, 60);
      pdf.text('Filtres appliqués:', 20, yPosition);
      yPosition += 5;
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(80, 80, 80);
      
      // Get the filter names for the report
      const timeframeText = selectedTimeframe === 'week' ? 'Cette semaine' : 
                           selectedTimeframe === 'month' ? 'Ce mois' : 
                           selectedTimeframe === 'quarter' ? 'Ce trimestre' : 'Cette année';
      const categoryText = selectedCategory === 'all' ? 'Toutes les catégories' : selectedCategory;
      
      pdf.text(`Période: ${timeframeText}   |   Catégorie: ${categoryText}`, 20, yPosition);
      yPosition += 15;
      
      // Executive Summary
      yPosition = await addSectionHeader('RÉSUMÉ DE PERFORMANCE', yPosition);
      
      // Calculate some overall metrics for the summary
      const avgScore = productPerformanceData.length > 0 
        ? Math.round(productPerformanceData.reduce((sum, product) => sum + product.score, 0) / productPerformanceData.length)
        : 0;
        
      const avgMargin = productPerformanceData.length > 0 
        ? (productPerformanceData.reduce((sum, product) => sum + parseFloat(product.margin), 0) / productPerformanceData.length).toFixed(1)
        : 0;
        
      const topProduct = productPerformanceData.length > 0 ? productPerformanceData[0] : null;
      const lowStockCount = productPerformanceData.filter(p => p.score < 40).length;
        
      const summaryData = [
        ['Score de performance moyen', `${avgScore}/100`],
        ['Marge moyenne', `${avgMargin}%`],
        ['Produit le plus performant', topProduct ? topProduct.name : 'N/A'],
        ['Produits à risque', `${lowStockCount}`]
      ];
      
      autoTable(pdf, {
        startY: yPosition,
        head: [['Métrique', 'Valeur']],
        body: summaryData,
        theme: 'plain',
        headStyles: { 
          fillColor: primaryRed, 
          textColor: 255, 
          fontSize: 9, 
          halign: 'left' 
        },
        styles: { 
          fontSize: 8, 
          cellPadding: 3, 
          lineColor: [220, 220, 220], 
          lineWidth: 0.1 
        },
        margin: { left: 20, right: 20 }
      });
      
      yPosition = pdf.lastAutoTable.finalY + 15;
      
      // Profitability Evolution
      yPosition = await addSectionHeader('ÉVOLUTION DE LA PROFITABILITÉ', yPosition);
      
      // Add chart description
      pdf.setFontSize(8);
      pdf.setTextColor(80, 80, 80);
      pdf.text("L'évolution de la profitabilité est représentée graphiquement dans l'application.", 20, yPosition);
      pdf.text("Ce rapport PDF présente les données numériques correspondantes.", 20, yPosition + 4);
      yPosition += 15;
      
      // Add profitability data table
      const profitabilityTableData = profitabilityData.map(item => [
        item.month,
        `${Math.round(item.sales).toLocaleString('fr-FR')} DA`,
        `${Math.round(item.profit).toLocaleString('fr-FR')} DA`,
        item.sales > 0 ? `${Math.round((item.profit / item.sales) * 100)}%` : '0%'
      ]);
      
      autoTable(pdf, {
        startY: yPosition,
        head: [['Mois', 'Ventes', 'Profit', 'Marge']],
        body: profitabilityTableData,
        theme: 'plain',
        headStyles: { 
          fillColor: primaryRed, 
          textColor: 255, 
          fontSize: 8, 
          halign: 'left' 
        },
        styles: { 
          fontSize: 7, 
          cellPadding: 2, 
          lineColor: [230, 230, 230], 
          lineWidth: 0.1 
        },
        alternateRowStyles: { fillColor: lightGray },
        margin: { left: 20, right: 20 }
      });
      
      yPosition = pdf.lastAutoTable.finalY + 15;
      
      // Products performance details
      yPosition = await addSectionHeader('DÉTAILS DE PERFORMANCE PAR PRODUIT', yPosition);
      
      if (productPerformanceData.length > 0) {
        // Format product performance data for the table
        const productTableData = productPerformanceData.slice(0, 15).map((product, index) => [
          (index + 1).toString(),
          product.name,
          product.category,
          `${Math.round(product.sales).toLocaleString('fr-FR')} DA`,
          `${product.margin}%`,
          product.turnover,
          `${product.score}/100`,
          product.trend === 'up' ? `+${product.trendValue}%` : `-${product.trendValue}%`
        ]);
        
        autoTable(pdf, {
          startY: yPosition,
          head: [['#', 'Produit', 'Catégorie', 'Ventes', 'Marge', 'Rotation', 'Score', 'Tendance']],
          body: productTableData,
          theme: 'plain',
          headStyles: { 
            fillColor: primaryRed, 
            textColor: 255, 
            fontSize: 8, 
            halign: 'left' 
          },
          styles: { 
            fontSize: 7, 
            cellPadding: 2, 
            lineColor: [230, 230, 230], 
            lineWidth: 0.1 
          },
          alternateRowStyles: { fillColor: lightGray },
          margin: { left: 20, right: 20 }
        });
        
        yPosition = pdf.lastAutoTable.finalY + 15;
      } else {
        pdf.setFontSize(9);
        pdf.setTextColor(120, 120, 120);
        pdf.text('Aucune donnée de performance par produit disponible.', 20, yPosition);
        yPosition += 10;
      }
      
      // Category performance
      yPosition = await addSectionHeader('PERFORMANCE PAR CATÉGORIE', yPosition);
      
      if (categoryPerformanceData.length > 0) {
        const categoryTableData = categoryPerformanceData.map((category, index) => [
          category.name,
          category.productCount.toString(),
          `${Math.round(category.totalSales).toLocaleString('fr-FR')} DA`,
          `${Math.round(category.totalProfit).toLocaleString('fr-FR')} DA`,
          `${category.averageScore}/100`
        ]);
        
        autoTable(pdf, {
          startY: yPosition,
          head: [['Catégorie', 'Nb. Produits', 'Ventes Totales', 'Profit Total', 'Score Moyen']],
          body: categoryTableData,
          theme: 'plain',
          headStyles: { 
            fillColor: primaryRed, 
            textColor: 255, 
            fontSize: 8, 
            halign: 'left' 
          },
          styles: { 
            fontSize: 7, 
            cellPadding: 2, 
            lineColor: [230, 230, 230], 
            lineWidth: 0.1 
          },
          alternateRowStyles: { fillColor: lightGray },
          margin: { left: 20, right: 20 }
        });
        
        yPosition = pdf.lastAutoTable.finalY + 15;
      } else {
        pdf.setFontSize(9);
        pdf.setTextColor(120, 120, 120);
        pdf.text('Aucune donnée de performance par catégorie disponible.', 20, yPosition);
        yPosition += 10;
      }
      
      // Insights and recommendations
      yPosition = await addSectionHeader('INSIGHTS ET RECOMMANDATIONS', yPosition);
      
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(60, 60, 60);
      
      // Top performers
      if (productRankings.top && productRankings.top.length > 0) {
        pdf.text('Produits performants', 20, yPosition);
        yPosition += 5;
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Les produits ${productRankings.top.slice(0, 3).map(p => `"${p.name}"`).join(', ')}`, 20, yPosition);
        yPosition += 4;
        pdf.text('montrent d\'excellentes performances. Considérez d\'augmenter votre investissement marketing', 20, yPosition);
        yPosition += 4;
        pdf.text('sur ces produits pour maximiser les revenus.', 20, yPosition);
        yPosition += 10;
      }
      
      // Underperformers
      if (productRankings.bottom && productRankings.bottom.length > 0) {
        pdf.setFont('helvetica', 'bold');
        pdf.text('Produits sous-performants', 20, yPosition);
        yPosition += 5;
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Les produits ${productRankings.bottom.slice(0, 3).map(p => `"${p.name}"`).join(', ')}`, 20, yPosition);
        yPosition += 4;
        pdf.text('présentent des performances inférieures aux attentes. Évaluez leur positionnement', 20, yPosition);
        yPosition += 4;
        pdf.text('prix ou envisagez de les retirer du catalogue si la tendance persiste.', 20, yPosition);
        yPosition += 10;
      }
      
      // Category insights
      if (categoryPerformanceData.length > 0) {
        const topCategory = [...categoryPerformanceData].sort((a, b) => b.averageScore - a.averageScore)[0];
        
        pdf.setFont('helvetica', 'bold');
        pdf.text('Insights par catégorie', 20, yPosition);
        yPosition += 5;
        pdf.setFont('helvetica', 'normal');
        pdf.text(`La catégorie "${topCategory.name}" montre les meilleures performances avec un score moyen de`, 20, yPosition);
        yPosition += 4;
        pdf.text(`${topCategory.averageScore}/100. Concentrez vos efforts d'expansion de catalogue sur cette catégorie.`, 20, yPosition);
      }
      
      // Add header/footer to all pages
      const totalPages = pdf.getNumberOfPages();
      for (let i = 2; i <= totalPages; i++) {
        pdf.setPage(i);
        currentPage = i;
        await addHeaderFooter();
      }
      
      // Generate filename and download
      const fileName = `Performance_Produits_${new Date().toISOString().slice(0, 10)}.pdf`;
      console.log('💾 Saving PDF:', fileName);
      
      pdf.save(fileName);
      
      setIsGenerating(false);
      toast.success('Rapport PDF généré avec succès!');
      console.log('✅ PDF generation completed successfully');
      
    } catch (error) {
      console.error('❌ PDF generation error:', error);
      setIsGenerating(false);
      toast.error(`Erreur lors de la génération du PDF: ${error.message}`);
    }
  };

  // Performance labels are displayed directly in the UI

  const getTrendIcon = (trend, value) => {
    if (trend === 'up') {
      return (
        <div className="flex items-center text-green-400">
          <ArrowTrendingUpIcon className="h-4 w-4 mr-1" />
          <span>+{value}%</span>
        </div>
      );
    } else {
      return (
        <div className="flex items-center text-red-400">
          <ArrowTrendingDownIcon className="h-4 w-4 mr-1" />
          <span>-{value}%</span>
        </div>
      );
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <div className="w-16 h-16 border-4 border-t-blue-500 border-white/20 rounded-full animate-spin"></div>
        <p className="mt-4 text-white/60">Analyse des performances en cours...</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* En-tête */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/advanced-analytics')}
            className="p-2 bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 text-white hover:bg-white/20 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">Analyse de Performance des Produits</h1>
            <p className="text-white/60 text-sm mt-1">
              Évaluation détaillée des performances et de la rentabilité de votre catalogue
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <button 
            onClick={handleGenerateReport}
            disabled={isGenerating}
            className="flex items-center space-x-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl border border-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <DocumentArrowDownIcon className="h-5 w-5" />
            <span>{isGenerating ? 'Génération...' : 'Exporter PDF'}</span>
          </button>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap gap-4 bg-white/5 p-4 rounded-xl border border-white/10">
        <div>
          <label className="text-white/60 text-sm mb-1 block">Période</label>
          <select
            value={selectedTimeframe}
            onChange={(e) => setSelectedTimeframe(e.target.value)}
            className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="week">Cette semaine</option>
            <option value="month">Ce mois</option>
            <option value="quarter">Ce trimestre</option>
            <option value="year">Cette année</option>
          </select>
        </div>

        <div>
          <label className="text-white/60 text-sm mb-1 block">Catégorie</label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {categories.map((category) => (
              <option key={category} value={category}>
                {category === 'all' ? 'Toutes les catégories' : category}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Tableau de bord principal */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Score de performance moyen */}
        <div className="bg-white/5 backdrop-blur-lg rounded-xl p-6 border border-white/20">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="text-lg font-medium text-white/80">Score de Performance</h3>
              <p className="text-3xl font-bold text-white mt-2">
                {productPerformanceData.length > 0 
                  ? Math.round(productPerformanceData.reduce((sum, product) => sum + product.score, 0) / productPerformanceData.length)
                  : 0}
                <span className="text-lg text-white/60 ml-1">/100</span>
              </p>
            </div>
            <div className="p-3 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600">
              <ChartBarIcon className="h-6 w-6 text-white" />
            </div>
          </div>
          <div className="w-full bg-white/10 rounded-full h-2.5">
            <div 
              className="bg-gradient-to-r from-blue-500 to-indigo-600 h-2.5 rounded-full" 
              style={{ width: `${productPerformanceData.length > 0 
                ? Math.round(productPerformanceData.reduce((sum, product) => sum + product.score, 0) / productPerformanceData.length) 
                : 0}%` }}
            ></div>
          </div>
          <p className="mt-2 text-white/60 text-sm">
            Performance globale de votre catalogue
          </p>
        </div>

        {/* Produit le plus performant */}
        <div className="bg-white/5 backdrop-blur-lg rounded-xl p-6 border border-white/20">
          <h3 className="text-lg font-medium text-white/80 mb-2">Produit le plus performant</h3>
          {productPerformanceData.length > 0 ? (
            <>
              <div className="flex items-center space-x-2">
                <StarIcon className="h-5 w-5 text-yellow-400" />
                <h4 className="text-lg font-semibold text-white">{productPerformanceData[0].name}</h4>
              </div>
              <p className="text-white/60 text-sm mt-1">{productPerformanceData[0].category}</p>
              <div className="mt-3 flex justify-between items-center">
                <div>
                  <p className="text-sm text-white/60">Score</p>
                  <p className={`text-xl font-bold ${getPerformanceColor(productPerformanceData[0].score)}`}>
                    {productPerformanceData[0].score}/100
                  </p>
                </div>
                <div>
                  <p className="text-sm text-white/60">Marge</p>
                  <p className="text-xl font-bold text-green-400">{productPerformanceData[0].margin}%</p>
                </div>
                <div>
                  <p className="text-sm text-white/60">Tendance</p>
                  {getTrendIcon(productPerformanceData[0].trend, productPerformanceData[0].trendValue)}
                </div>
              </div>
            </>
          ) : (
            <p className="text-white/60 text-center py-4">Aucune donnée disponible</p>
          )}
        </div>

        {/* Produits à risque */}
        <div className="bg-white/5 backdrop-blur-lg rounded-xl p-6 border border-white/20">
          <div className="flex items-center space-x-2 mb-4">
            <ExclamationTriangleIcon className="h-5 w-5 text-amber-400" />
            <h3 className="text-lg font-medium text-white/80">Produits à risque</h3>
          </div>
          {productPerformanceData.length > 0 ? (
            <div className="space-y-3">
              {productPerformanceData
                .filter(product => product.score < 40)
                .slice(0, 3)
                .map((product, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-8 bg-red-500/50 rounded-full"></div>
                      <div>
                        <p className="text-white text-sm font-medium">{product.name}</p>
                        <p className="text-white/60 text-xs">{product.category}</p>
                      </div>
                    </div>
                    <div className="text-red-400 font-medium">{product.score}/100</div>
                  </div>
                ))}
              {productPerformanceData.filter(product => product.score < 40).length === 0 && (
                <div className="flex items-center justify-center py-4 space-x-2">
                  <CheckCircleIcon className="h-5 w-5 text-green-400" />
                  <p className="text-green-400">Aucun produit à risque</p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-white/60 text-center py-4">Aucune donnée disponible</p>
          )}
        </div>
      </div>

      {/* Graphiques de performance */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Graphique de profitabilité */}
        <div className="bg-white/5 backdrop-blur-lg rounded-xl p-6 border border-white/20">
          <h3 className="text-lg font-medium text-white mb-4">Évolution de la profitabilité</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={profitabilityData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="month" stroke="rgba(255,255,255,0.6)" />
                <YAxis stroke="rgba(255,255,255,0.6)" />
                <Tooltip 
                  formatter={(value) => `${value.toLocaleString()} DA`}
                  contentStyle={{ 
                    backgroundColor: 'rgba(30, 41, 59, 0.9)', 
                    borderColor: 'rgba(255,255,255,0.2)',
                    color: 'white'
                  }}
                />
                <Legend wrapperStyle={{ color: 'white' }} />
                <Line type="monotone" dataKey="sales" name="Ventes" stroke="#8884d8" strokeWidth={2} />
                <Line type="monotone" dataKey="profit" name="Profit" stroke="#82ca9d" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Graphique de performance par catégorie */}
        <div className="bg-white/5 backdrop-blur-lg rounded-xl p-6 border border-white/20">
          <h3 className="text-lg font-medium text-white mb-4">Performance par catégorie</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart outerRadius={90} data={categoryPerformanceData}>
                <PolarGrid stroke="rgba(255,255,255,0.2)" />
                <PolarAngleAxis dataKey="name" stroke="rgba(255,255,255,0.6)" />
                <PolarRadiusAxis stroke="rgba(255,255,255,0.6)" />
                <Radar
                  name="Score de performance"
                  dataKey="averageScore"
                  stroke="#8884d8"
                  fill="#8884d8"
                  fillOpacity={0.6}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(30, 41, 59, 0.9)', 
                    borderColor: 'rgba(255,255,255,0.2)',
                    color: 'white'
                  }}
                />
                <Legend wrapperStyle={{ color: 'white' }} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Tableau des produits */}
      <div className="bg-white/5 backdrop-blur-lg rounded-xl border border-white/20 overflow-hidden">
        <div className="p-6 border-b border-white/10">
          <h3 className="text-lg font-medium text-white">Classement des produits par performance</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white/5">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Rang</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Produit</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Catégorie</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Ventes</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Marge</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Rotation</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-white/70 uppercase tracking-wider">Score</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-white/70 uppercase tracking-wider">Tendance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {productPerformanceData.slice(0, 10).map((product, index) => (
                <tr key={index} className="hover:bg-white/5">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">{index + 1}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{product.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white/80">{product.category}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white text-right">
                    {product.sales.toLocaleString()} DA
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white text-right">
                    {product.margin}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white text-right">
                    {product.turnover}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="flex items-center justify-center">
                      <span className={`inline-block w-8 h-8 rounded-full flex items-center justify-center ${
                        product.score >= 80 ? 'bg-green-500/20' :
                        product.score >= 60 ? 'bg-blue-500/20' :
                        product.score >= 40 ? 'bg-yellow-500/20' :
                        'bg-red-500/20'
                      }`}>
                        <span className={`text-sm font-medium ${getPerformanceColor(product.score)}`}>
                          {product.score}
                        </span>
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                    {getTrendIcon(product.trend, product.trendValue)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Insights et recommandations */}
      <div className="bg-white/5 backdrop-blur-lg rounded-xl p-6 border border-white/20">
        <h3 className="text-xl font-semibold text-white mb-4">Insights et Recommandations</h3>

        <div className="space-y-4">
          {/* Top performers */}
          <div className="p-4 bg-gradient-to-r from-green-500/20 to-green-700/20 rounded-xl border border-green-500/30">
            <h4 className="font-medium text-green-300 mb-2">Produits performants</h4>
            <p className="text-sm text-white/80">
              {productRankings.top && productRankings.top.length > 0 ? (
                <>
                  Les produits {productRankings.top.slice(0, 3).map(p => `"${p.name}"`).join(', ')} 
                  montrent d'excellentes performances. Considérez d'augmenter votre investissement marketing 
                  sur ces produits pour maximiser les revenus.
                </>
              ) : (
                'Aucune donnée disponible pour les produits performants.'
              )}
            </p>
          </div>

          {/* Underperformers */}
          <div className="p-4 bg-gradient-to-r from-red-500/20 to-red-700/20 rounded-xl border border-red-500/30">
            <h4 className="font-medium text-red-300 mb-2">Produits sous-performants</h4>
            <p className="text-sm text-white/80">
              {productRankings.bottom && productRankings.bottom.length > 0 ? (
                <>
                  Les produits {productRankings.bottom.slice(0, 3).map(p => `"${p.name}"`).join(', ')} 
                  présentent des performances inférieures aux attentes. Évaluez leur positionnement 
                  prix ou envisagez de les retirer du catalogue si la tendance persiste.
                </>
              ) : (
                'Aucune donnée disponible pour les produits sous-performants.'
              )}
            </p>
          </div>

          {/* Category insights */}
          <div className="p-4 bg-gradient-to-r from-blue-500/20 to-blue-700/20 rounded-xl border border-blue-500/30">
            <h4 className="font-medium text-blue-300 mb-2">Insights par catégorie</h4>
            <p className="text-sm text-white/80">
              {categoryPerformanceData.length > 0 ? (
                <>
                  La catégorie "{categoryPerformanceData.sort((a, b) => b.averageScore - a.averageScore)[0].name}" 
                  montre les meilleures performances avec un score moyen de 
                  {categoryPerformanceData.sort((a, b) => b.averageScore - a.averageScore)[0].averageScore}/100. 
                  Concentrez vos efforts d'expansion de catalogue sur cette catégorie.
                </>
              ) : (
                'Aucune donnée disponible pour les insights par catégorie.'
              )}
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ProductPerformanceAnalysis;